/**
 * components/EmptyState/FlowStrip
 * ──────────────────────────────────────────────────────────────────────────
 * "How the data flows": four boxes in ONE non-wrapping row with `→` between
 * them, the current screen's box inverted to ink.
 *
 * The design is emphatic that this row must not wrap: a wrapped row leaves an
 * arrow pointing at nothing, which turns an explanation into a glitch. So each
 * box is `flex: 1 1 0` with `minWidth: 0` and shrinks instead of wrapping, and
 * on a phone — where four boxes genuinely cannot fit — the strip becomes a
 * VERTICAL flow with the arrows rotated to match, which is the same diagram
 * read top-to-bottom rather than a broken horizontal one.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { mono, sans, size as fontSize } from '../../theme/typography';
import { useResponsive } from '../../lib/responsive';

export interface FlowNode {
  key: string;
  label: string;
  sub: string;
  /** The box for the screen the user is looking at — inverted to ink. */
  here?: boolean;
}

export interface FlowStripProps {
  nodes: readonly FlowNode[];
}

export function FlowStrip({ nodes }: FlowStripProps) {
  const { isMobile } = useResponsive();

  return (
    <View style={isMobile ? styles.columnStrip : styles.rowStrip}>
      {nodes.map((node, index) => (
        <View
          key={node.key}
          style={isMobile ? styles.columnItem : styles.rowItem}
        >
          <View
            style={[
              styles.box,
              node.here ? styles.boxHere : styles.boxPlain,
              !isMobile && styles.boxFlex,
            ]}
          >
            <Text numberOfLines={1} style={[styles.label, node.here && styles.labelHere]}>
              {node.label}
            </Text>
            <Text numberOfLines={1} style={[styles.sub, node.here && styles.subHere]}>
              {node.sub}
            </Text>
          </View>
          {index < nodes.length - 1 && (
            <Text style={styles.arrow}>{isMobile ? '↓' : '→'}</Text>
          )}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  // `flexWrap: 'nowrap'` is the whole point — see the header comment.
  rowStrip: { flexDirection: 'row', alignItems: 'stretch', flexWrap: 'nowrap', gap: 8 },
  columnStrip: { flexDirection: 'column', gap: 6 },
  rowItem: { flexDirection: 'row', alignItems: 'center', gap: 8, flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
  columnItem: { gap: 6 },
  box: {
    paddingVertical: 9,
    paddingHorizontal: 11,
    borderWidth: 1,
    borderRadius: radii.box,
  },
  boxFlex: { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
  boxPlain: { borderColor: neutral.border, backgroundColor: neutral.surface },
  boxHere: { borderColor: neutral.ink, backgroundColor: neutral.ink },
  label: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  labelHere: { color: neutral.surface },
  sub: { ...sans('400'), fontSize: fontSize.metaSm, color: neutral.grey600 },
  // On the inverted box the sub line is grey-300, not grey-600 — it has to
  // read against ink.
  subHere: { color: neutral.grey300 },
  arrow: { ...mono('400'), fontSize: fontSize.body, color: neutral.grey300, alignSelf: 'center' },
});

export default FlowStrip;
